import { ModuleHeader } from '@/components'
import { Button, Input, PhoneInput, Select } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { Form, FormField } from '@/components_deprecated/Form'
import { Label } from '@/components_deprecated/Label'
import { emailValidation } from '@/utils/forms'
import { useBreadcrumbs, useCompany, useOnErrorAuto } from '@/utils/hooks'
import { useMutation } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { parsePhoneNumber } from 'libphonenumber-js'
import React, { useEffect } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import type { CreateUserMutation, CreateUserMutationVariables } from '.'
import { CREATE_USER_MUTATION } from './mutations'
import type { AreaProps } from './UserProfile/components'

const Layout = styled.div`
  ${props => props.theme.layout.default};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  .form-card {
    width: clamp(600px, 60vw, 90%);
  }

  ${props => props.theme.layout.mobile} {
    .form-card {
      width: 100%;
    }
  }
`

const GridForm = styled(Form)`
  width: 100%;
  padding: 1rem;
  display: grid;
  grid-gap: 1rem;

  & > div + div {
    margin-top: 0;
  }

  grid-template-areas:
    'firstName lastName'
    'phone email'
    'userType leaderGroup'
    'submit submit';

  .submit {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
  }

  ${props => props.theme.layout.mobile} {
    grid-template-areas:
      'firstName'
      'lastName'
      'email'
      'phone'
      'userType'
      'leaderGroup'
      'submit';
  }
`

const SubmitButton = styled(Button)`
  white-space: nowrap;
`

const GridFormField = styled(FormField)<AreaProps>`
  grid-area: ${props => props.$area};
`

interface CreateUserForm {
  company: string
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  userType: string
  leaderGroup: string | null
}

export const CreateUser: React.FC = () => {
  const translations = useTranslate({
    create: 'users.create-user',
    newUser: 'users.new',
    error: {
      email: 'error.valid-email-required',
      fieldRequired: ['error.field-is-required', { field: '' }],
      invalidPhoneNumber: 'error.invalid-phone-number',
    },
    fields: {
      email: 'common.email',
      firstName: 'common.firstName',
      lastName: 'common.lastName',
      phoneNumber: 'common.phone-number',
      userType: 'common.user-type',
      leaderGroup: 'common.leader-group',
    },
    loading: 'common.loading',
    toasts: {
      createError: ['toasts.error.create-type', { type: '' }],
      successAdd: ['toasts.success-add-type', { type: '' }],
      invitationSent: 'users.invitation-sent',
      alreadyMember: 'users.already-member',
    },
    user: 'common.user',
  })

  const navigate = useNavigate()

  const company = useCompany()

  const { setOverrides, overrides } = useBreadcrumbs()
  const addToast = useToast()

  const validateFieldNotEmpty = (errorMessage: string | null) => {
    return (value: string | null) => (value ? null : errorMessage)
  }

  const firstUserType = company.userTypes[0]?.id
  // Should never happen
  if (!firstUserType)
    throw new Error('No user types found for company ' + company.id)

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    updateForm,
    submitHandler,
    formChangeHandler: handler,
  } = useForm<CreateUserForm>({
    values: {
      company: company.id,
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '+47',
      userType: firstUserType,
      leaderGroup: null,
    },
    validators: {
      email: email =>
        emailValidation(email) ? null : translations.error.email,
      firstName: validateFieldNotEmpty(
        translations.error.fieldRequired({
          field: translations.fields.firstName,
        })
      ),
      lastName: validateFieldNotEmpty(
        translations.error.fieldRequired({
          field: translations.fields.lastName,
        })
      ),
      phoneNumber(value) {
        try {
          return parsePhoneNumber(value)?.isValid()
            ? null
            : translations.error.invalidPhoneNumber
        } catch {
          return translations.error.invalidPhoneNumber
        }
      },
      userType: validateFieldNotEmpty(
        translations.error.fieldRequired({
          field: translations.fields.lastName,
        })
      ),
    },
  })

  function userTypeHandler<K extends keyof typeof form>(key: K) {
    return (value: (typeof form)[K] | null) => {
      if (value === null) return
      updateForm({ [key]: value })
    }
  }

  const onErrorAuto = useOnErrorAuto()

  const [createUserMutation, { loading }] = useMutation<
    CreateUserMutation,
    CreateUserMutationVariables
  >(CREATE_USER_MUTATION, {
    onCompleted: data => {
      const { createUser } = data

      // FD-520: the email already belonged to an existing TaCtrl user, who has
      // been emailed a request to join this company. Nothing is created until
      // they accept.
      if (createUser.invitationSent) {
        addToast('success', translations.toasts.invitationSent)
        void navigate('/users')
        return
      }

      // The existing user is already a member of this company.
      if (createUser.alreadyMember) {
        addToast('warning', translations.toasts.alreadyMember)
        return
      }

      if (createUser.ok) {
        addToast(
          'success',
          translations.toasts.successAdd({
            type: translations.user.toLowerCase(),
          })
        )
        void navigate('/users')
        return
      }

      addToast(
        'error',
        translations.toasts.createError({
          type: translations.user.toLowerCase(),
        })
      )
    },
    onError: onErrorAuto(
      {},
      {
        showToast: false,
        callback() {
          addToast(
            'error',
            translations.toasts.createError({
              type: translations.user.toLowerCase(),
            })
          )
        },
      }
    ),
  })

  function submitForm(values: typeof form) {
    void createUserMutation({
      variables: {
        ...values,
      },
    })
  }
  const userTypeOptions = [...company.userTypes]
    .sort((a, b) => {
      if (a.name < b.name) return -1
      if (a.name > b.name) return 1
      return 0
    })
    .map(type => ({
      value: type.id,
      label: type.name,
    }))

  const leaderGroupOptions = [...company.leaderGroups]
    .sort((a, b) => {
      if (a.name < b.name) return -1
      if (a.name > b.name) return 1
      return 0
    })
    .map(type => ({
      value: type.id,
      label: type.name,
    }))

  useEffect(() => {
    if (overrides['create']) return
    setOverrides({ create: translations.newUser })
  }, [overrides, setOverrides, translations.newUser])

  return (
    <Layout>
      <ModuleHeader title={translations.newUser} />

      <Card className="form-card">
        <GridForm preventDefault onSubmit={submitHandler(submitForm)}>
          <GridFormField $area="email">
            <Label htmlFor="email">{translations.fields.email}</Label>
            <Input
              id="email"
              width="100%"
              background="inherit"
              tabIndex={4}
              value={form.email}
              error={errors.email}
              onChange={handler('email')}
            />
          </GridFormField>

          <GridFormField $area="firstName">
            <Label htmlFor="firstName">{translations.fields.firstName}</Label>
            <Input
              id="firstName"
              width="100%"
              background="inherit"
              autoFocus
              tabIndex={1}
              value={form.firstName}
              error={errors.firstName}
              onChange={handler('firstName')}
            />
          </GridFormField>

          <GridFormField $area="lastName">
            <Label htmlFor="lastName">{translations.fields.lastName}</Label>
            <Input
              id="lastName"
              width="100%"
              background="inherit"
              tabIndex={1}
              value={form.lastName}
              error={errors.lastName}
              onChange={handler('lastName')}
            />
          </GridFormField>
          <GridFormField $area="phone">
            <Label htmlFor="phoneNumber">
              {translations.fields.phoneNumber}
            </Label>
            <PhoneInput
              id="phoneNumber"
              width="100%"
              background="inherit"
              tabIndex={2}
              value={form.phoneNumber}
              error={errors.phoneNumber}
              onChange={handler('phoneNumber')}
            />
          </GridFormField>

          <GridFormField $area="userType">
            <Label htmlFor="userType">{translations.fields.userType}</Label>
            <Select
              id="userType"
              width="100%"
              background="white"
              tabIndex={5}
              value={form.userType}
              error={errors.userType}
              options={userTypeOptions}
              onChange={userTypeHandler('userType')}
            />
          </GridFormField>

          {leaderGroupOptions.length > 0 && (
            <GridFormField $area="leaderGroup">
              <Label htmlFor="leaderGroup">
                {translations.fields.leaderGroup}
              </Label>
              <Select
                id="leaderGroup"
                width="100%"
                background="white"
                tabIndex={6}
                value={form.leaderGroup}
                options={leaderGroupOptions}
                onChange={handler('leaderGroup')}
                placeholder={translations.fields.leaderGroup}
                nullable
              />
            </GridFormField>
          )}

          <GridFormField $area="submit" className="submit">
            <SubmitButton
              width="200px"
              tabIndex={6}
              disabled={loading || !formValid}
            >
              {loading ? translations.loading : translations.create}
            </SubmitButton>
          </GridFormField>
        </GridForm>
      </Card>
    </Layout>
  )
}
